import { Card } from 'flowbite-react'
import type { PropsWithChildren } from 'react'
import React, { memo } from 'react'

import imagesManifest from '@/generated/images-manifest.json'

import './index.css'

export interface ServiceCardProps {
  title: string
  imgSrc: string
}

// Card images span the full card width, so at the lg breakpoint (3-col grid)
// each one is roughly a third of the viewport; below that it's full width.
const IMAGE_SIZES = '(min-width: 1024px) 33vw, 100vw'

interface ImageVariant {
  format: string
  width: number
  file: string
}

interface ImageManifestEntry {
  width: number
  height: number
  aspectRatio: number
  fallback: string
  variants: ImageVariant[]
}

const manifest: Record<string, ImageManifestEntry> = imagesManifest

const ResponsiveCardImage: React.FC<{ imgSrc: string; alt: string }> = ({
  imgSrc,
  alt,
}) => {
  const entry = manifest[imgSrc]
  if (!entry) {
    return <img src={imgSrc} alt={alt} className="rounded-t-lg" />
  }
  const srcSet = entry.variants
    .map((v: ImageVariant) => `/${v.file} ${v.width}w`)
    .join(', ')
  return (
    <picture>
      <source type="image/webp" srcSet={srcSet} sizes={IMAGE_SIZES} />
      <img
        src={`/${entry.fallback}`}
        alt={alt}
        width={entry.width}
        height={entry.height}
        loading="lazy"
        decoding="async"
        className="rounded-t-lg"
      />
    </picture>
  )
}

const ServiceCard: React.FC<PropsWithChildren<ServiceCardProps>> = memo(
  ({ children, title, imgSrc }) => {
    return (
      <Card
        id="card"
        className=" bg-red-400"
        renderImage={() => <ResponsiveCardImage imgSrc={imgSrc} alt={title} />}
      >
        <h3 className="text-2xl font-bold tracking-tight text-yellow-100">
          {title}
        </h3>
        <p className="font-normal text-left text-yellow-50">{children}</p>
        <div className="h-full" />
      </Card>
    )
  },
)
ServiceCard.displayName = 'ServiceCard'

export default ServiceCard
